/**
 * Statistics - Figma `6377:87435` (and `6377:87449`, `6377:87970`).
 *
 * A two-column head - a large heading on the left, a paragraph on the right - above a
 * row of orange tiles. Each tile carries its label at the top and the figure at the
 * bottom, so the figures line up along a baseline however tall the tiles get.
 *
 * **The tiles are 1-2-2-1**, the designer's rule as of 2026-09-26: the file draws the
 * two inner tiles 440 wide and the outer two sharing what is left, 216 each. It read
 * like tiles sized by their own figures ("70" narrow, "ok. 2830" wide) and was built that
 * way, with `flex-auto`, until the designer said otherwise. Any other count keeps the
 * rule: the first and last tile one share, every one between them two.
 *
 * `description` is in the content model but not in this mock-up. It renders under the
 * figure when an editor fills it in, rather than being silently dropped.
 */
import type { CSSProperties } from 'react';
import { CtaLink, Eyebrow, SectionShell, withHighlight } from '../primitives';
import type { StatsSection } from '../../types/sections';

/** One share for the outer tiles, two for each one between them: `1fr 2fr 2fr 1fr` for four. */
function statsColumns(count: number): string {
  if (count <= 2) return `repeat(${count}, minmax(0, 1fr))`;
  return ['1fr', ...Array<string>(count - 2).fill('2fr'), '1fr'].map((share) => `minmax(0, ${share})`).join(' ');
}

export default function Stats({ data, first = false }: { data: StatsSection; first?: boolean }) {
  // Spotkania and Wyjazdy motywacyjne open with this block (6377:87435, 6377:87449),
  // where its title is the page title at Display-01. Elsewhere it is an h2 inside one.
  const Title = first ? 'h1' : 'h2';

  return (
    <SectionShell settings={data.settings} name="stats">
      {data.eyebrow ? <Eyebrow value={data.eyebrow} /> : null}

      {data.heading || data.body ? (
        <div className="grid gap-8 lg:grid-cols-2 lg:gap-16">
          {data.heading ? (
            <Title className={`text-h2 text-ink ${first ? 'lg:text-display' : 'lg:text-h1'}`}>{withHighlight(data.heading)}</Title>
          ) : null}
          <div className="flex flex-col items-start gap-8">
            {data.body ? <p className="whitespace-pre-line text-body text-ink-muted">{data.body}</p> : null}
            {/* The button sits at the foot of the right column, opposite the lead
                (3883:71464), not under the whole head. */}
            {data.cta ? <div className="mt-auto"><CtaLink value={data.cta} /></div> : null}
          </div>
        </div>
      ) : null}

      {data.stats?.length ? (
        <div
          className="grid gap-4 pt-8 sm:grid-cols-2 lg:[grid-template-columns:var(--stats-columns)]"
          style={{ '--stats-columns': statsColumns(data.stats.length) } as CSSProperties}
        >
          {data.stats.map((stat, i) => (
            <div
              key={i}
              className="flex min-h-[190px] flex-col justify-between gap-8 rounded-s bg-orange-500 p-5 text-ink-strong"
            >
              {/* h4 over h1-Medium, 26 and 60 (6377:87442). */}
              <p className="text-h4">{stat.label}</p>
              <div>
                <p className="text-h1 font-medium">{stat.value}</p>
                {stat.description ? <p className="mt-2 text-body-sm">{stat.description}</p> : null}
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </SectionShell>
  );
}
